import type { MenuProps } from '@mui/material';
import { Box, Menu, Stack } from '@mui/material';
import { MenuButton } from './MenuButton';
import { Button, colors } from '@sweep-io/sweep-design';
import usePopoverMenu from '../../../../hooks/usePopover';
import { EnvMenuItems } from './EnvMenuItems';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectConnectedCrmOrgs } from '../environmentsReducer';
import ConnectToSfDialog from '../../get-started/connect-org/ConnectToSfDialog';
import { TruncatedTextTooltip } from '../../../common/TruncatedTextTooltip';
import WithConnectOrgPermission from '../../../common/WithConnectOrgPermission';
import { useDefaultEnv } from '../default-env-context/DefaultEnvContext';

import { ChevronDown } from '@sweep-io/sweep-design/dist/icons/ChevronDown';
import { ChevronUp } from '@sweep-io/sweep-design/dist/icons/ChevronUp';

enum ConnectDialogType {
  REGULAR = 'REGULAR',
  FORCE_PRODUCTION = 'FORCE_PRODUCTION',
}

export const EnvSelector = ({
  variant = 'full',
  menuProps,
  onChangeEnvCallback,
  disabled,
  disableManageEnv,
  allowedEnvIds,
  disableConnectProd,
}: {
  variant?: 'full' | 'text' | 'system';
  menuProps?: Pick<MenuProps, 'anchorOrigin' | 'transformOrigin'>;
  disabled?: boolean;
  onChangeEnvCallback?: () => void;
  disableManageEnv?: boolean;
  allowedEnvIds?: string[];
  disableConnectProd?: boolean;
} = {}) => {
  const { isPopoverOpen, closePopover, openPopover, anchorElement, anchorRef } = usePopoverMenu();
  const [openedConnectDialogType, setOpenedConnectDialogType] = useState<ConnectDialogType | null>(
    null,
  );
  const selectedEnv = useDefaultEnv();
  const environments = useSelector(selectConnectedCrmOrgs);

  const onConnectProdClick = () => {
    closePopover();
    setOpenedConnectDialogType(ConnectDialogType.FORCE_PRODUCTION);
  };

  const closeSimpleDialog = useCallback(() => {
    setOpenedConnectDialogType(null);
  }, []);

  const onConnectToSfClick = useCallback(() => {
    closePopover();
    setOpenedConnectDialogType(ConnectDialogType.REGULAR);
  }, [closePopover]);

  const renderSimpleConnectDialog = () => {
    if (openedConnectDialogType) {
      return (
        <ConnectToSfDialog
          closeDialog={closeSimpleDialog}
          forceProduction={openedConnectDialogType === ConnectDialogType.FORCE_PRODUCTION}
          onFinish={closeSimpleDialog}
        />
      );
    }
  };

  const envName = selectedEnv?.name ?? '';

  const renderButton = () => {
    switch (variant) {
      case 'full':
        return (
          <MenuButton
            anchorRef={anchorRef}
            onClick={openPopover}
            pressed={isPopoverOpen}
            disabled={disabled}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                minWidth: 0,
                width: '135px',
              }}
            >
              <TruncatedTextTooltip variant={'body'} color={colors.grey[900]}>
                {envName}
              </TruncatedTextTooltip>
            </Box>
          </MenuButton>
        );
      case 'text':
        return (
          <Button
            onClick={openPopover}
            ref={anchorRef}
            variant="flat-grey"
            size="small"
            pressed={isPopoverOpen}
            endIcon={isPopoverOpen ? ChevronUp : ChevronDown}
          >
            {envName}
          </Button>
        );
      case 'system':
        return (
          <Button
            onClick={openPopover}
            ref={anchorRef}
            variant="system"
            size="small"
            pressed={isPopoverOpen}
            endIcon={isPopoverOpen ? ChevronUp : ChevronDown}
            disabled={disabled}
          >
            {envName}
          </Button>
        );
    }
  };

  const renderEmptyButton = () => (
    <Stack sx={{ '& > button': { whiteSpace: 'nowrap' } }}>
      <WithConnectOrgPermission>
        <Button size="small" onClick={onConnectToSfClick}>
          Connect to Salesforce
        </Button>
      </WithConnectOrgPermission>
    </Stack>
  );

  const onDoubleClick = useCallback(() => {
    if (selectedEnv) {
      navigator.clipboard.writeText(selectedEnv.id);
      console.log('Copied current salesforce org id to clipboard', selectedEnv.id);
    }
  }, [selectedEnv]);

  return (
    <>
      <Box onDoubleClick={onDoubleClick}>
        <Box sx={{ '& svg': { width: '12px', height: '12px' } }}>
          {environments.length ? renderButton() : renderEmptyButton()}
        </Box>
        <Menu
          id="user-menu"
          anchorEl={anchorElement}
          open={isPopoverOpen}
          onClose={() => closePopover()}
          MenuListProps={{
            'aria-labelledby': 'user-menu-button',
          }}
          slotProps={{
            paper: {
              sx: (theme) => ({
                marginTop: '5px',
                width: 'auto',
                boxShadow: theme.shadows[7],
              }),
            },
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          {...menuProps}
        >
          <EnvMenuItems
            onConnectProdClick={onConnectProdClick}
            close={closePopover}
            onChangeEnvCallback={onChangeEnvCallback}
            disableManageEnv={disableManageEnv}
            allowedEnvIds={allowedEnvIds}
            disableConnectProd={disableConnectProd}
          />
        </Menu>
      </Box>
      {renderSimpleConnectDialog()}
    </>
  );
};
